import {
    BlurEvent,
    Button,
    ChangeEvent,
    ClickEvent,
    DataSource,
    DataSourceExecutionEvent,
    DataSourceMode,
    DropdownSelectionEvent,
    Layout,
    LookupModelSelectionEvent,
    SaveButton,
    Switch,
    TableRow,
    TableRowDisplayEvent,
    Textbox
} from "@mcleod/components";
import {
    Alignment,
    Api,
    CompanySettings,
    Currency,
    CurrencyProps,
    CurrencySettings,
    ModelRow,
    StringUtil
} from "@mcleod/core";
import {CommonTooltips} from "@mcleod/common";
import {getDispatchControlBoolean} from "@mcleod/general";
import {ModelEdiorderProfile, RowEdiorderProfile} from "@mcleod/datafusion";
import {MovementCarrierPay} from "@mcleod/powerbroker";
import {ModelCustomer} from "@mcleod/ar";
import {AutogenLayoutEdiBillingRates} from "./autogen/AutogenLayoutEdiBillingRates";
import {ChargeCodeQuickInfo, OtherChargesUtil, RateUtil, TaxUtil} from "@mcleod/dispatch";
import {DistanceAlertUtil} from "@mcleod/dispatch/src/DistanceAlertUtil";

export class EdiBillingRates extends AutogenLayoutEdiBillingRates {

    private _allocationEnabled: boolean;
    private _calculatingCharges = false;
    private chargePanelExpanded: boolean = true;
    private ratingDetailsPanelExpanded: boolean = false;
    private taxPanelExpanded: boolean = false;
    private estimateFuelSurcharge: boolean = getDispatchControlBoolean("est_fuel_surcharge")
    private autoRate: boolean = getDispatchControlBoolean("auto_rate");
    private otherChargesUtil: OtherChargesUtil = new OtherChargesUtil();
    private taxUtil: TaxUtil = new TaxUtil();
    private _invokedFromEdi: boolean = false;
    private _currencyFromProfile: string = null;
    private _currencyTypeFromQuote: string;
    private distanceAlertUtil = new DistanceAlertUtil();
    private _isBalanceDue: boolean = false;
    doAfterRating: () => void;
    checkPickupAndDeliveryEntered = () => true;

    override onLoad(): void {
        this.textboxCollectionMethod.items = RateUtil.getCollectionMethodDropdownItems();
        this.textboxCtrlParty.textCombined.manualAddLayout = null;
        this.buttonEstimateFuelSurcharge.visible = !this.estimateFuelSurcharge;
        this.otherChargesUtil.initialize(this);
        this.taxUtil.initialize(this);
        this.initializeRatingDetails();
        this.textboxCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
        if (!CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxCurrencyType.defaultDataValue = CurrencySettings.getFunctionalCurrency();
        } else {
            this.panelTaxes.visible = true;
        }
        if (!CompanySettings.getSingleton().isAsset()) {
            this.tableOtherCharges.removeColumn(this.tableOtherCharges.columns.find(col => "columnHeaderDriver" === col.headingCell.id));
        }

        // Set up listeners for manual charge adjustments
        this.setupManualChargeListeners();
    }

    private setupManualChargeListeners() {
        console.log(">>> setupManualChargeListeners called");

        // Freight charge changes should recalculate totals immediately when user tabs out
        this.textboxFreightCharge.addBlurListener(async (event) => {
            console.log(">>> textboxFreightCharge blur event fired", {
                userInitiatedChange: event.userInitiatedChange,
                changedWhileFocused: event.changedWhileFocused,
                mode: DataSourceMode[this.mainDataSource.mode],
                isAddingOrUpdating: this.mainDataSource.isAddingOrUpdating()
            });
            // Use isAddingOrUpdating() instead of mode check, and changedWhileFocused instead of userInitiatedChange
            if (this.mainDataSource.isAddingOrUpdating() && event.changedWhileFocused) {
                await this.recalculateTotalsFromUserEdit();
            }
        });

        // Rate changes should recalculate freight charge immediately when user tabs out
        // Also clear rate_id to indicate user override (mimics Java swing behavior)
        this.textboxRate.addBlurListener(async (event) => {
            console.log(">>> textboxRate blur event fired", {
                userInitiatedChange: event.userInitiatedChange,
                changedWhileFocused: event.changedWhileFocused,
                mode: DataSourceMode[this.mainDataSource.mode],
                isAddingOrUpdating: this.mainDataSource.isAddingOrUpdating()
            });
            // Use isAddingOrUpdating() instead of mode check, and changedWhileFocused instead of userInitiatedChange
            if (this.mainDataSource.isAddingOrUpdating() && event.changedWhileFocused) {
                this.clearRateId();
                await this.recalculateFreightChargeFromRate();
            }
        });

        // Rating units changes should recalculate freight charge immediately when user tabs out
        // Also clear rate_id to indicate user override (mimics Java swing behavior)
        this.textboxRateUnits.addBlurListener(async (event) => {
            console.log(">>> textboxRateUnits blur event fired", {
                userInitiatedChange: event.userInitiatedChange,
                changedWhileFocused: event.changedWhileFocused,
                mode: DataSourceMode[this.mainDataSource.mode],
                isAddingOrUpdating: this.mainDataSource.isAddingOrUpdating()
            });
            // Use isAddingOrUpdating() instead of mode check, and changedWhileFocused instead of userInitiatedChange
            if (this.mainDataSource.isAddingOrUpdating() && event.changedWhileFocused) {
                this.clearRateId();
                await this.recalculateFreightChargeFromRate();
            }
        });

        // Distance changes may affect rate units if rate type is Distance
        // Also clear rate_id to indicate user override (mimics Java swing behavior)
        this.textboxBillDistance.addBlurListener(async (event) => {
            console.log(">>> textboxBillDistance blur event fired", {
                userInitiatedChange: event.userInitiatedChange,
                changedWhileFocused: event.changedWhileFocused,
                mode: DataSourceMode[this.mainDataSource.mode]
            });
            if (this.mainDataSource.isAddingOrUpdating() && event.changedWhileFocused) {
                this.clearRateId();
                // If rate type is Distance, update rating units to match distance
                const rateType = this.mainDataSource.activeRow?.get("rate_type");
                if (rateType === "D" || rateType === "Distance") {
                    const distance = this.mainDataSource.activeRow?.get("distance");
                    if (distance != null) {
                        this.mainDataSource.activeRow?.set("rating_units", distance);
                        this.mainDataSource.displayDataInBoundComponent(this.textboxRateUnits);
                        await this.recalculateFreightChargeFromRate();
                    }
                }
            }
        });

        // Rate type changes should:
        // 1. Update field visibility (Other type shows unit description field)
        // 2. Set rating units based on rate type (mimics Java RowBilling logic)
        // 3. Clear rate_id to indicate user override
        // 4. Recalculate freight charge
        this.textboxRateType.addAfterDropdownSelectionListener(async (event: DropdownSelectionEvent) => {
            console.log(">>> textboxRateType dropdown selection event fired", {
                userInitiatedChange: event.userInitiatedChange,
                mode: DataSourceMode[this.mainDataSource.mode],
                newValue: event.selection?.value
            });
            if (this.mainDataSource.isAddingOrUpdating() && event.userInitiatedChange) {
                const rateType = event.selection?.value;
                const isOther = rateType === "Other" || rateType === "O";
                this.textboxRateUnitDesc.visible = isOther;

                // Clear rate ID when user manually changes rate type
                this.clearRateId();

                // Set rating units based on rate type (mimics RowBilling.java logic)
                await this.setRatingUnitsForRateType(rateType);

                // Recalculate freight charge with new units
                await this.recalculateFreightChargeFromRate();
            }
        });

        console.log(">>> setupManualChargeListeners completed - listeners attached to:", {
            freight: this.textboxFreightCharge?.id,
            rate: this.textboxRate?.id,
            units: this.textboxRateUnits?.id,
            distance: this.textboxBillDistance?.id,
            rateType: this.textboxRateType?.id
        });
    }

    async sourceOrdersAfterExecution(event: DataSourceExecutionEvent) {
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.panelExciseTaxTotal.visible = true;
            this.panelTotalWithTaxes.visible = true;
            if (this.mainDataSource.mode !== DataSourceMode.ADD) {
                const transactionalCurrency = await this.getTransactionalCurrency();
                const orderCurrencyCode = this.mainDataSource.activeRow?.get("linehaul_chg_c", transactionalCurrency) as string;
                this.mainDataSource.activeRow?.setLookupModelData("linehaul_chg_c", { currency_type_id: orderCurrencyCode });
                this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
                this.otherChargesUtil.currencyType = orderCurrencyCode;
                this.taxUtil.currencyType = orderCurrencyCode;
            }
        }
        const rateId = this.mainDataSource.activeRow?.get("rate_id", null);
        const origDestRateId = this.sourceOrders.activeRow?.get("orig_dest_rate_id", null);
        if (rateId != null && origDestRateId != null) {
            RateUtil.setAutoRateContext(this, this.mainDataSource.activeRow?.get("rate_id"), this.sourceOrders.activeRow?.get("orig_dest_rate_id"));
        }
        const segAllocCode = this.sourceOrders.activeRow?.get("seg_alloc_code");
        this.textboxSegAllocCode.visible = segAllocCode != null;
    }

    async sourceEdiBillingDetailAfterExecution(event: DataSourceExecutionEvent) {
        this.tableEdiDriverExtraPay.visible = this.mainDataSource.activeRow?.get("direction") === "I";
        this.tableOtherCharges.visible = this.mainDataSource.activeRow?.get("direction") === "O";
    }

    setMaxTargetPayData() {
        const maxPayData = this.mainDataSource.activeRow?.get("max_target_pay_data");
        if (maxPayData != null && this.carrierPayLayout?.visible == true) {
            this.setupMaxTargetPanel(false);
            this.carrierPayLayout.setDataFromResponse(maxPayData);
            this.mainDataSource.activeRow.set("max_target_pay_data", undefined);
        }
    }

    getOtherChargeDatasource(): DataSource {
        return this.sourceOtherChargeEdi;
    }

    getTaxDatasource(): DataSource {
        return this.sourceExcisetaxChargeEdi;
    }

    get carrierPayLayout(): MovementCarrierPay {
        return this.layoutMovementRates as MovementCarrierPay
    }

    set calculatingCharges(value: boolean) {
        this._calculatingCharges = value;
    }

    get calculatingCharges(): boolean {
        return this._calculatingCharges;
    }

    get currencyTypeFromQuote(): string {
        return this._currencyTypeFromQuote;
    }

    set currencyTypeFromQuote(currencyTypeFromQuote: string) {
        this._currencyTypeFromQuote = currencyTypeFromQuote;
    }

    get isBalanceDue(): boolean {
        return this._isBalanceDue;
    }

    set isBalanceDue(value: boolean) {
        this._isBalanceDue = value;
    }

    public getTaxTable() {
        return this.tableTax;
    }

    textboxSegAllocCodeBeforeLookupModelSearch(event) {
        event.filter.alloc_type = 'M';
        event.filter.is_active = 'Y';
    }

    buttonExpandChargesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.chargePanelExpanded) this.panelOtherChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 90
        }); else this.panelOtherChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 0
        });
        this.chargePanelExpanded = !this.chargePanelExpanded;
    }

    buttonExpandTaxesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.taxPanelExpanded) this.panelTaxTable.collapse(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 90
        }); else this.panelTaxTable.expand(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 0
        });
        this.taxPanelExpanded = !this.taxPanelExpanded;
    }

    buttonExpandDetailsOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.ratingDetailsPanelExpanded) this.panelRatingDetailsTable.collapse(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 90
        }); else this.panelRatingDetailsTable.expand(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 0
        });
        this.ratingDetailsPanelExpanded = !this.ratingDetailsPanelExpanded;
    }

    async sourceOrdersAfterModeChange(event) {
        if ((event.newMode == DataSourceMode.ADD || event.newMode == DataSourceMode.UPDATE) && event.newMode != event.oldMode) {
            this.setupMaxTargetPanel(false);
            this.sourceOtherChargeEdi.mode = DataSourceMode.ADD;
        }
    }

    private setupMaxTargetPanel(visible: boolean) {
        this.carrierPayLayout.visible = visible;
        this.panelRateTotals.borderBottomRightRadius = visible ? 0 : 4;
        this.panelRateTotals.borderBottomLeftRadius = visible ? 0 : 4;
        if (this.carrierPayLayout.mainDataSource.activeRow == null) this.carrierPayLayout.configureForOrderEntry(this.sourceOrders, true);
    }

    async otherChargesOnBlur(event: BlurEvent) {
        await this.otherChargesUtil.otherChargeOnBlur(event);

        // Immediately recalculate other charge total when user finishes editing
        await this.recalculateOtherChargeTotal();
    }

    async taxesOnBlur(event: BlurEvent) {
        await this.taxUtil.taxOnBlur(event);

        // Immediately recalculate totals when user finishes editing tax field
        await this.recalculateOtherChargeTotal();
    }

    buttonEstimateFuelSurchargeOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, false, true);
    }

    buttonRateOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, true);
    }

    ratingButtonClicked(event: ClickEvent, autoRate: boolean, estimateFsc: boolean = this.estimateFuelSurcharge) {
        const button = event.target as Button;
        button.busy = true;
        this.calculateRates(autoRate, estimateFsc).finally(() => {
            button.busy = false
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && this.otherChargesUtil.currencyType) {
                this.convertPricingCurrencies(this.otherChargesUtil.currencyType);
            }
        });
    }

    async textboxChargeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            await this.otherChargesUtil.textboxChargeIdOnLookupModelSelection(event, this.mainDataSource.activeRow.get("id"));

            // Immediately recalculate other charge total when charge type is selected
            await this.recalculateOtherChargeTotal();
        }
    }

    async textboxExciseTaxIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            await this.taxUtil.textboxExciseTaxIdOnLookupModelSelection(event, this.mainDataSource.activeRow.get("id"));
        }
    }

    async tableOtherChargesOnContentsChanged(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.otherChargesUtil.tableOtherChargeBillOnContentsChanged();

        // Immediately recalculate and update other charge totals
        await this.recalculateOtherChargeTotal();

        // Then perform full rate calculation
        await this.calculateRates(false, false);
    }

    async tableTaxOnContentsChanged(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.taxUtil.tableTaxOnContentsChanged();

        // Immediately recalculate totals when tax charges change
        await this.recalculateOtherChargeTotal();

        this.calculateRates(false, false).finally(() => {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && this.otherChargesUtil.currencyType) {
                this.convertPricingCurrencies(this.otherChargesUtil.currencyType);
            }
        });
    }

    async calculateRates(autorate: boolean = this.autoRate, affectRateId: boolean = true): Promise<any> {
        console.log(">>> calculateRates called", {
            isAddingOrUpdating: this.mainDataSource.isAddingOrUpdating(),
            mode: DataSourceMode[this.mainDataSource.mode]
        });

        if (!this.mainDataSource.isAddingOrUpdating() || this.calculatingCharges || this.mainDataSource.activeRow == null || !this.checkPickupAndDeliveryEntered()) {
            console.log(">>> calculateRates SKIPPED");
            return Promise.resolve();
        }
        const billingWithAll: ModelRow = this.mainDataSource.activeRow;
        this.mainDataSource.fillLinkedValues(billingWithAll, null);
        const billingRow: ModelRow = this.sourceEdiBillingDetail.getDataboundValues(null, true, null, null);
        const stopRows: ModelRow[] = this.mainDataSource.childDataSources.find((source) => source.id == "sourceStop")?.data
        // Exempt the header's Save/Save & Close button from the DataSource busy toggle. It shares this
        // DataSource (via inheritParentDataSource), so setComponentsBusy() would otherwise flip it busy and
        // tear down its just-opened dropdown overlay, causing the "Save" option to flicker on the first click.
        const saveButton: SaveButton = this.getHeaderSaveButton();
        const saveButtonBusyReaction: boolean = saveButton?.busyWhenDataSourceBusy;
        if (saveButton != null)
            saveButton.busyWhenDataSourceBusy = false;
        this.mainDataSource.setComponentsBusy(true);
        this.calculatingCharges = true;
        try {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                if (this.currencyTypeFromQuote) {
                    this.convertPricingCurrencies(this.currencyTypeFromQuote);
                } else if (billingRow.get("recurring_order_id") && this.activeRow?.getCurrency("linehaul_chg").getCurrencyCode() !==
                    this.otherChargesUtil.currencyType) {
                    this.convertPricingCurrencies(this.activeRow?.getCurrency("linehaul_chg").getCurrencyCode());
                } else if (this.otherChargesUtil.currencyType && this.activeRow?.getCurrency("linehaul_chg").getCurrencyCode() !==
                    this.otherChargesUtil.currencyType) {
                    this.convertPricingCurrencies(this.otherChargesUtil.currencyType);
                }
                this.mainDataSource.activeRow?.set("linehaul_chg", this.activeRow?.get("linehaul_chg"));
            }
            const body = {
                "autorate": autorate, "billing_row": billingWithAll
            };
            const response = await Api.search("lme/ar/calculate-billing-charges", body);

            let totalChargeChanged = false;
            const data = response.data[0];
            if (!data) return;
            const billingApiData = data["billing_row"] || [];

            this.setBillDistanceWarnings(data);

            if (stopRows != null) {
                const stopApiData = data["stop_data"] || [];
                stopApiData.forEach((stop, index) => {
                    if (stopRows[index]?.data) {
                        stopRows[index].data["rate_dist_from_previous"] = stop["rate_dist_from_previous"];
                    }
                });
            }

            // Auto Rate
            if (autorate) {
                for (let i = this.tableOtherCharges.rows.length - 1; i >= 0; i--) {
                    const row = this.tableOtherCharges.rows[i];
                    if (row?.data?.data["rate_id"] != null) {
                        row.deleteRow()
                    }
                }
            }

            if (autorate) {
                const appendingId: string[] = [];
                this.tableOtherCharges.rows.forEach(row => {
                    if (row?.data?._appending == false) appendingId.push(row.data?._data?.["id"])
                });
                this.sourceOtherChargeEdi.data = [];
                const otherCharges = data["other_charges"] || [];
                otherCharges.forEach(element => {
                    const isAppending = appendingId.length == 0 || !appendingId.includes(element["id"]);
                    const modelRow = new ModelRow(this.sourceOtherChargeEdi.url, isAppending);
                    modelRow.setValues(element);
                    this.sourceOtherChargeEdi.data.push(modelRow);
                });
                this.tableOtherCharges.displayData(null, this.sourceOtherChargeEdi.data, 0);
            } else if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                this.tableOtherCharges.displayData(null, this.sourceOtherChargeEdi.data, 0);
            }

            this.textboxRateType.setSelectedItemFromValue(billingApiData["rate_type"]);
            this.mainDataSource.activeRow?.set("rate_type", billingApiData["rate_type"]);
            this.mainDataSource.activeRow?.set("distance", billingApiData["distance"]);
            this.mainDataSource.activeRow?.set("rating_method", billingApiData["rating_method"]);
            this.mainDataSource.activeRow?.set("rate", billingApiData["rate"]);
            this.mainDataSource.activeRow?.set("rating_units", billingApiData["rating_units"]);

            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                const tempCurrency: Currency = new Currency(billingApiData["linehaul_chg"] as CurrencyProps);
                tempCurrency.setConversionDate(this.activeRow.getCurrency("linehaul_chg")?.getConversionDate());
                if (!StringUtil.isEmptyString(this.otherChargesUtil.currencyType) && billingApiData["linehaul_chg_c"] != this.otherChargesUtil.currencyType)
                    tempCurrency?.setCurrencyCode(this.otherChargesUtil.currencyType);
                this.activeRow?.set("linehaul_chg", tempCurrency);
            } else if (billingApiData["linehaul_chg"] != null &&
                (this.activeRow?.getCurrency("linehaul_chg")?.amount != billingApiData["linehaul_chg"]?.amount
                    || this.activeRow?.getCurrency("linehaul_chg")?.currency_code != billingApiData["linehaul_chg"]?.currency_code)) {
                this.mainDataSource.activeRow?.set("linehaul_chg", billingApiData["linehaul_chg"]);
                this.mainDataSource.activeRow?.set("linehaul_chg_n", billingApiData["linehaul_chg_n"]);
            }
            if (billingApiData["total_charges"] != null) {
                if (billingApiData["total_charges"]?.amount != this.mainDataSource.activeRow?.get("total_charge")?.amount || 0) {
                    this.mainDataSource.activeRow?.set("total_charges", billingApiData["total_charges"]);
                    this.mainDataSource.activeRow?.set("total_charges_n", billingApiData["total_charges_n"]);
                    totalChargeChanged = true;
                }
            }

            // For balance due transactions, recalculate total charges to include other charges
            // This is especially important when the freight charge is zero
            const currentFreightCharge = this.mainDataSource.activeRow?.getCurrency("linehaul_chg");
            const currentOtherCharge = this.mainDataSource.activeRow?.getCurrency("other_charge");

            if (currentFreightCharge && currentOtherCharge) {
                const freightAmount = currentFreightCharge.amount || 0;
                const otherAmount = currentOtherCharge.amount || 0;
                const recalculatedTotal = freightAmount + otherAmount;

                // Update the total charges if it differs from the calculated total
                const currentTotalCharges = this.mainDataSource.activeRow?.getCurrency("total_charges");
                if (currentTotalCharges && currentTotalCharges.amount !== recalculatedTotal) {
                    currentTotalCharges.amount = recalculatedTotal;
                    this.mainDataSource.activeRow?.set("total_charges", currentTotalCharges);
                    // Also update the numeric field for database persistence
                    this.mainDataSource.activeRow?.set("total_charges_n", recalculatedTotal);
                    totalChargeChanged = true;
                }
            }

                        // Calculate and set other charges and tax totals
            const otherChargeTotal = await this.otherChargesUtil.getOtherChargeBillTotal(false);
            const taxTotal = await this.taxUtil.getTaxChargeBillTotal(false);
            const taxTotalWithCharges = await this.taxUtil.getTaxChargeBillTotal(true);

            this.mainDataSource.activeRow?.set("other_charge", otherChargeTotal);
            this.mainDataSource.activeRow?.set("excisetax_total", taxTotal);
            this.mainDataSource.activeRow?.set("totalcharge_and_excisetax", taxTotalWithCharges);

            if (affectRateId) {
                const origDestRateId = data["orig_dest_rate_id"] || billingRow.get("orig_dest_rate_id", null);
                RateUtil.setAutoRateContext(this, billingApiData["rate_id"], origDestRateId);
            }
            if (totalChargeChanged && this.doAfterRating != null) this.doAfterRating();
        } finally {
            this.calculatingCharges = false;
            this.mainDataSource.setComponentsBusy(false);
            if (saveButton != null)
                saveButton.busyWhenDataSourceBusy = saveButtonBusyReaction;
        }
    }

    /**
     * Locates the Save/Save & Close button in the surrounding DataHeader. It's bound to this layout's
     * mainDataSource (shared with the parent via inheritParentDataSource), so it appears in the
     * DataSource's bound components.
     */
    private getHeaderSaveButton(): SaveButton {
        return this.mainDataSource?.boundComponents?.find((component) => component instanceof SaveButton) as SaveButton;
    }

    /**
     * Sets the warning state for the bill distance textbox based on the response from the Model.
     * If there is an error message in the response, it sets the textbox properties to display a warning.
     * Otherwise, it resets the properties to default values.
     *
     * @param response - The response from the Model: lme/dispatch/calculate-order-charges.
     */
    private setBillDistanceWarnings(response: any) {
        const errorMessage = response?.["message"];
        if (errorMessage) {
            this.textboxBillDistance.color = "warning";
            this.textboxBillDistance.borderColor = "warning";
            this.textboxBillDistance.borderWidth = 1;
            this.textboxBillDistance.tooltip = errorMessage;
            this.textboxBillDistance.allowTooltip = true;
        } else {
            this.textboxBillDistance.color = "default";
            this.textboxBillDistance.borderColor = undefined;
            this.textboxBillDistance.borderWidth = undefined;
            this.textboxBillDistance.tooltip = undefined;
            this.textboxBillDistance.allowTooltip = false;
        }
    }

    /**
     * Recalculates and updates the other charge total immediately when charges are modified.
     * This ensures the EDI billing record reflects changes without waiting for full rate calculation.
     */
    private async recalculateOtherChargeTotal(): Promise<void> {
        if (!this.mainDataSource.isAddingOrUpdating() || this.mainDataSource.activeRow == null) {
            return;
        }

        try {
            // Calculate the other charge total from the current table data
            const otherChargeTotal = await this.otherChargesUtil.getOtherChargeBillTotal(false);

            // Update the other charge total on the main data source
            this.mainDataSource.activeRow?.set("other_charge", otherChargeTotal);

            // Recalculate total charges to include the updated other charges
            const currentFreightCharge = this.mainDataSource.activeRow?.getCurrency("linehaul_chg");
            const currentOtherCharge = this.mainDataSource.activeRow?.getCurrency("other_charge");

            if (currentFreightCharge && currentOtherCharge) {
                const freightAmount = currentFreightCharge.amount || 0;
                const otherAmount = currentOtherCharge.amount || 0;
                const recalculatedTotal = freightAmount + otherAmount;

                // Update the total charges
                const currentTotalCharges = this.mainDataSource.activeRow?.getCurrency("total_charges");
                if (currentTotalCharges) {
                    currentTotalCharges.amount = recalculatedTotal;
                    this.mainDataSource.activeRow?.set("total_charges", currentTotalCharges);
                    // Also update the numeric field for database persistence
                    this.mainDataSource.activeRow?.set("total_charges_n", recalculatedTotal);
                }
            }

            // Force display update of the components
            this.mainDataSource.displayDataInBoundComponent(this.textboxOtherchargetotal);
            this.mainDataSource.displayDataInBoundComponent(this.textboxTotalCharge);

        } catch (error) {
            console.error("Error recalculating other charge total:", error);
        }
    }

    /** This is an event handler for the onChange event of checkboxLockMiles.  */
    checkboxLockMilesOnChange(event) {
        this.textboxBillDistance.enabled = event.newValue === false;
    }

    sourceRatingDetailsAfterExecution(event: DataSourceExecutionEvent) {
        this.initializeRatingDetails();
    }

    /** This is an event handler for the afterExecution event of sourceDistanceAlert. */
    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        this.distanceAlertUtil.checkForDistanceAlertsTextbox(this.sourceDistanceAlert, this.textboxBillDistance);
    }

    initializeRatingDetails() {
        this.switchByFreightItem.checked = true;
        this.tableRevSummary.visible = false;
        this.panelRatingDetailsTable.collapse({ speed: 100, paddingTop: 0 }).then(() => {
            this.buttonExpandDetails.imageRotation = 90;
        });
    }

    switchByFreightItemOnClick(event: ClickEvent) {
        const switchByFreightItem = event.target as Switch;
        this.tableRevPerFgi.visible = switchByFreightItem.checked;
        this.tableRevSummary.visible = !switchByFreightItem.checked;
    }

    tableRevPerFgiOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        this.addBuySellTooltips(tableRow, "textboxFgiRevenueAmount");
    }

    tableRevSummaryOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        this.addBuySellTooltips(tableRow, "textboxRevenueAmount");
    }

    addBuySellTooltips(tableRow: TableRow, componentId: string) {
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const buyRateModelRow = new ModelRow(modelRow._modelPath, false, modelRow.data);
        buyRateModelRow.set("charge_as_calc_description", modelRow.get("buy_rate_description"));
        buyRateModelRow.set("tariff_id", modelRow.get("tariff_id"));
        buyRateModelRow.set("tli_uid", modelRow.get("tli_uid"));
        if (buyRateModelRow.get("tariff_id", null) != null) {
            const component = tableRow.findComponentById(componentId);
            if (component != null) {
                CommonTooltips.setTooltipContentLayout(component, buyRateModelRow, "lme/dispatch/RevenueDetailQuickInfo");
                component.tooltipDynamicOptions = { position: Alignment.TOP };
            }
        }
    }

    async textboxChargeCalcMethodOnDropdownSelection(event: DropdownSelectionEvent) {
        if (event.selection?.value === "D") {
            const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
            const distance = this.sourceOrders.activeRow.get("bill_distance", null);
            await this.setChargeDistanceUnits(tableRow, distance);
            // Note: setChargeDistanceUnits will trigger recalculation when it updates the amount
        }
        // For other calc methods, no immediate recalculation needed since amount isn't auto-calculated
    }


    textboxMethodOnDropdownSelection(event: DropdownSelectionEvent) {
        // Rate units field stays enabled - users can edit for any rate type
    }

    async setChargeDistanceUnits(tableRow: TableRow, distance: string) {
        const units = tableRow.findComponentById("textboxChargeUnits") as Textbox;
        units.text = distance;
        const rate = tableRow.data.get("rate");
        if (rate != null) {
            const amount = tableRow.findComponentById("textboxChargeAmount") as Textbox;
            amount.text = (parseFloat(rate) * parseFloat(distance)).toFixed(2);

            // Update the model data as well
            const modelRow = tableRow.data as ModelRow;
            modelRow.set("amount", parseFloat(amount.text));

            // Immediately recalculate other charge total since amount was changed
            await this.recalculateOtherChargeTotal();
        }
    }

    /** Event handler for when charge amount is changed - PRIMARY TRIGGER FOR OC RECALCULATION */
    async textboxChargeAmountOnChange(event: ChangeEvent) {
        // Immediately recalculate other charge total when amount changes
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when charge amount loses focus - SECONDARY TRIGGER FOR OC RECALCULATION */
    async textboxChargeAmountOnBlur(event: BlurEvent) {
        // Immediately recalculate other charge total when leaving amount field
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when charge units are changed - affects calculated amounts */
    async textboxChargeUnitsOnChange(event: ChangeEvent) {
        // Immediately recalculate other charge total when units change (may affect calculated amounts)
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when charge units lose focus */
    async textboxChargeUnitsOnBlur(event: BlurEvent) {
        // Immediately recalculate other charge total when leaving units field
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when charge rate is changed - affects calculated amounts */
    async textboxChargeRateOnChange(event: ChangeEvent) {
        // Immediately recalculate other charge total when rate changes (may affect calculated amounts)
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when charge rate loses focus */
    async textboxChargeRateOnBlur(event: BlurEvent) {
        // Immediately recalculate other charge total when leaving rate field
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when tax amount is changed - PRIMARY TRIGGER FOR TAX RECALCULATION */
    async textboxTaxAmountOnChange(event: ChangeEvent) {
        // Immediately recalculate totals when tax amount changes
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when tax amount loses focus - SECONDARY TRIGGER FOR TAX RECALCULATION */
    async textboxTaxAmountOnBlur(event: BlurEvent) {
        // Immediately recalculate totals when leaving tax amount field
        await this.recalculateOtherChargeTotal();
    }

    /** Event handler for when the "Include in Freight" switch is toggled */
    async switchIncludeInFreightOnClick(event: ClickEvent) {
        // Immediately recalculate totals when include in freight setting changes
        await this.recalculateOtherChargeTotal();
    }

    async textboxCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", await this.getTransactionalCurrency()) as string;
            this.convertPricingCurrencies(currencyType);
        }
    }

    convertPricingCurrencies(currencyType: string) {
        this.otherChargesUtil.currencyType = currencyType;
        this.taxUtil.currencyType = currencyType;
        const freightCharge: Currency = this.mainDataSource.activeRow?.getCurrency("linehaul_chg");
        freightCharge?.setCurrencyCode(currencyType);
        freightCharge?.setConversionDate(freightCharge.getConversionDate());
        const otherCharge: Currency = this.mainDataSource.activeRow?.getCurrency("other_charge");
        otherCharge?.setCurrencyCode(currencyType);
        const totalCharge: Currency = this.mainDataSource.activeRow?.getCurrency("total_charges");
        totalCharge?.setCurrencyCode(currencyType);
        const totalTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("excisetax_total");
        totalTaxes?.setCurrencyCode(currencyType);
        const totalWithTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("totalcharge_and_excisetax");
        totalWithTaxes?.setCurrencyCode(currencyType);
        this.mainDataSource.activeRow?.setValues({
            "linehaul_chg": freightCharge, "other_charge": otherCharge, "total_charges": totalCharge,
            "excisetax_total": totalTaxes, "totalcharge_and_excisetax": totalWithTaxes
        });
        this.updateCurrencySelector(currencyType);
        this.tableOtherCharges.rows.forEach((tableRow) => {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                modelRow.set("amount", amount);
                tableRow.displayComponentValues();
            }
        });
        this.tableTax.rows.forEach((tableRow) => {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                modelRow.set("amount", amount);
                tableRow.displayComponentValues();
            }
        });
        const quickAddData: ModelRow = this.tableOtherCharges.quickAddData;
        if (quickAddData) {
            const amount: Currency = quickAddData.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                quickAddData.set("amount", amount);
                this.tableOtherCharges.quickAddRow.displayComponentValues();
            }
        }
        const taxQuickAddData: ModelRow = this.tableTax.quickAddData;
        if (taxQuickAddData) {
            const amount: Currency = taxQuickAddData.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                taxQuickAddData.set("amount", amount);
                this.tableTax.quickAddRow.displayComponentValues();
            }
        }
    }

    async getTransactionalCurrency(): Promise<string> {
        let transactionCurrency = CurrencySettings.getTransactionalCurrency();
        const partner = this.sourceOrders.activeRow?.getString("partner_id");
        const version = this.sourceOrders.activeRow?.getString("partner_version");
        if (this.getInvokedFromEdi() && this.getCurrencyFromProfile()) {
            transactionCurrency = this.getCurrencyFromProfile();
        } else if (partner && version) {
            const profile: RowEdiorderProfile = await new ModelEdiorderProfile().searchSingle({
                partner_id: partner,
                version: version
            });
            if (profile.data.currency_type_id) {
                transactionCurrency = profile.data.currency_type_id;
            }
        }
        return transactionCurrency;
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }

    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }

    public set currencyFromProfile(value: string) {
        this._currencyFromProfile = value;
    }

    public getCurrencyFromProfile(): string {
        return this._currencyFromProfile;
    }

    public beforeTextboxStopLookupModelSearch(event) {
        event.filter.order_id = this.sourceOrders.activeRow.get("id");
        event.filter.stop_type = "in PU,SO";
    }

    textboxStopNumberOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true)
            this.otherChargesUtil.textboxStopNumberOnLookupModelSelection(event);
    }

    textboxStopNumberOnChange(event: ChangeEvent) {
        this.otherChargesUtil.textboxStopNumberOnChange(event);
    }

    private updateCurrencySelector(currencyCode: string) {
        this.mainDataSource.activeRow?.set("linehaul_chg_c", currencyCode);
        this.mainDataSource.activeRow?.setLookupModelData("linehaul_chg_c", { currency_type_id: currencyCode });
        this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
    }

    async additionalChargesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const customerId = modelRow.get("customer_id");

        const switchIncludeInFreight = tableRow.findComponentById("switchIncludeInFreight") as Switch;
        if (!StringUtil.isEmptyString(customerId)) {
            switchIncludeInFreight.enabled = false;

            // Handle customer name display for rows with customer_id
            await this.displayCustomerNameInRow(tableRow, modelRow);
            return;
        }
        const isIncludeInFreight = this.mainDataSource?.activeRow?.get("customer.allow_inc_in_frt", "N") === "Y";

        // Configure switch in the current row
        if (switchIncludeInFreight) {
            switchIncludeInFreight.enabled = isIncludeInFreight;
        }

        // Configure switch in the quick add row if it exists
        if (this.tableOtherCharges?.quickAddRow) {
            const quickAddSwitch = this.tableOtherCharges.quickAddRow
                .findComponentById("switchIncludeInFreight") as Switch;

            if (quickAddSwitch) {
                quickAddSwitch.enabled = isIncludeInFreight;
            }
        }
        const chargeIdTextBox = tableRow.findComponentById("textboxChargeId") as Textbox;
        if (chargeIdTextBox) {
            this.setChargeCodeQuickInfo(chargeIdTextBox);
        }
    }

    async taxMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        await this.taxUtil.taxOnDropdownSelection(event);

        // Immediately recalculate totals when tax method changes
        await this.recalculateOtherChargeTotal();
    }

    async setChargeCodeQuickInfo(component: Textbox) {
        component.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            const equipLayout = layout as ChargeCodeQuickInfo;
            equipLayout.textboxPartnerValue.visible = true;
            await equipLayout.mainDataSource?.search({ search: keyValue });
        };
    }

    private async displayCustomerNameInRow(tableRow: TableRow, modelRow: ModelRow): Promise<void> {
        const customerId = modelRow.get("customer_id") as string;

        if (!customerId) {
            return;
        }

        const customerTextbox = tableRow.findComponentById("textboxCustomerId") as Textbox;

        if (!customerTextbox) {
            return;
        }

        try {
            let customerName: string;

            // Strategy 1: Check if lookup data is already available
            const lookupData = modelRow?.get("_lookup_customer_id");
            if (lookupData) {
                customerName = lookupData.name;
            }

            // Strategy 3: Look up customer name from the server
            if (!customerName) {
                try {
                    const customerRow = await new ModelCustomer().searchSingle({ id: customerId });
                    if (customerRow) {
                        customerName = customerRow.get("name");
                    }
                } catch (error) {
                    console.warn("Could not fetch customer name for ID:", customerId, error);
                }
            }

            // Work with the textbox's lookup model system for proper display
            if (customerName) {
                if (customerTextbox.hasLookupModel()) {
                    // Create a lookup model row with the customer data
                    const mockLookupData = customerTextbox.createLookupModelRow({
                        id: customerId,
                        name: customerName,
                        company_name: customerName
                    });

                    // Clear existing data and set our customer data
                    customerTextbox.clear();
                    modelRow.set("customer_id", customerId, customerTextbox);
                    modelRow.setLookupModelData("customer_id", [mockLookupData]);
                    customerTextbox.displayComponentData(modelRow, null, 0);
                } else {
                    // Fallback to setting text directly if no lookup model
                    customerTextbox.text = customerName;
                }
            } else {
                // Fall back to customer ID if no name available
                customerTextbox.text = customerId;
            }
        } catch (error) {
            console.warn(`Error loading customer name for ID ${customerId}:`, error);
            // Keep original value on error
        }
    }

    tableTaxOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        const descriptionTextbox = tableRow.findComponentById("textboxDescrTax") as Textbox;
        const rateTextBox = tableRow.findComponentById("textboxRateTax") as Textbox;
        const methodTextbox = tableRow.findComponentById("textboxMethod") as Textbox;
        const customerTextBox = tableRow.findComponentById("textboxCustomerIdTax") as Textbox;
        const textboxTaxDate = tableRow.findComponentById("textboxTaxDate") as Textbox;

        descriptionTextbox.enabled = this.isBalanceDue;
        rateTextBox.enabled = this.isBalanceDue;
        methodTextbox.enabled = this.isBalanceDue;
        customerTextBox.enabled = this.isBalanceDue;
        textboxTaxDate.enabled = this.isBalanceDue;
    }

    setOtherChargesUtilCurrency(currency: string) {
        this.otherChargesUtil.currencyType = currency;
    }

    /**
     * Clears the rate_id field to indicate user has manually overridden auto-rated values.
     * This mimics the Java swing EntryBilling.removeRateNumber() behavior.
     */
    private clearRateId(): void {
        console.log(">>> clearRateId called");
        if (this.mainDataSource.activeRow) {
            this.mainDataSource.activeRow.set("rate_id", null);
            this.labelAutoRated.visible = false;
        }
    }

    /**
     * Sets rating_units based on rate_type, mimicking RowBilling.java logic:
     * - Flat (F): rating_units = 1
     * - Distance (D): rating_units = distance
     * - CWT (C): rating_units = weight / 100
     * - Tons (T): rating_units = weight / 2000
     * - Other (O): rating_units unchanged (user editable)
     */
    private async setRatingUnitsForRateType(rateType: string): Promise<void> {
        console.log(">>> setRatingUnitsForRateType called", { rateType });

        if (!this.mainDataSource.activeRow) {
            return;
        }

        let newUnits: number | null = null;

        if (rateType === "F" || rateType === "Flat") {
            // Flat rate: units = 1
            newUnits = 1;
        } else if (rateType === "D" || rateType === "Distance") {
            // Distance rate: units = distance
            const distance = this.mainDataSource.activeRow.get("distance") as number;
            newUnits = distance ?? null;
        } else if (rateType === "C" || rateType === "CWT") {
            // CWT rate: units = weight / 100
            const weight = this.mainDataSource.activeRow.get("weight") as number;
            const minWeight = this.mainDataSource.activeRow.get("minimum_weight") as number;
            if (weight != null || minWeight != null) {
                const effectiveWeight = weight ?? minWeight ?? 0;
                newUnits = effectiveWeight / 100;
            }
        } else if (rateType === "T" || rateType === "Tons") {
            // Tons rate: units = weight / 2000
            const weight = this.mainDataSource.activeRow.get("weight") as number;
            const minWeight = this.mainDataSource.activeRow.get("minimum_weight") as number;
            if (weight != null || minWeight != null) {
                const effectiveWeight = weight ?? minWeight ?? 0;
                newUnits = effectiveWeight / 2000;
            }
        } else if (rateType === "O" || rateType === "Other") {
            // Other: don't change units, let user specify
            console.log(">>> Rate type is Other, rating_units unchanged");
            return;
        }

        if (newUnits != null) {
            console.log(">>> Setting rating_units to", newUnits);
            this.mainDataSource.activeRow.set("rating_units", newUnits);
            this.mainDataSource.displayDataInBoundComponent(this.textboxRateUnits);
        } else {
            console.log(">>> Setting rating_units to null (no applicable value)");
            this.mainDataSource.activeRow.set("rating_units", null);
            this.mainDataSource.displayDataInBoundComponent(this.textboxRateUnits);
        }
    }

    /**
     * Recalculates freight charge from rate and units when user manually edits those fields.
     * Formula: freight_charge = rate * units
     * This gives immediate feedback as users edit rate-related fields.
     */
    private async recalculateFreightChargeFromRate(): Promise<void> {
        console.log(">>> recalculateFreightChargeFromRate called", {
            mode: DataSourceMode[this.mainDataSource.mode],
            hasActiveRow: this.mainDataSource.activeRow != null
        });

        if (!this.mainDataSource.isAddingOrUpdating() || this.mainDataSource.activeRow == null) {
            console.log(">>> recalculateFreightChargeFromRate SKIPPED - not in UPDATE mode or no active row");
            return;
        }
        try {
            const rate = this.mainDataSource.activeRow?.get("rate") as number;
            const units = this.mainDataSource.activeRow?.get("rating_units") as number;

            console.log(">>> recalculateFreightChargeFromRate values:", { rate, units });

            if (rate != null && units != null && rate > 0 && units > 0) {
                const calculatedFreight = rate * units;
                console.log(">>> Calculated freight:", calculatedFreight);

                // Update the freight charge
                const currentFreightCharge = this.mainDataSource.activeRow?.getCurrency("linehaul_chg");
                if (currentFreightCharge) {
                    currentFreightCharge.amount = calculatedFreight;
                    this.mainDataSource.activeRow?.set("linehaul_chg", currentFreightCharge);
                    this.mainDataSource.activeRow?.set("linehaul_chg_n", calculatedFreight);
                }

                // Force display update of freight charge
                this.mainDataSource.displayDataInBoundComponent(this.textboxFreightCharge);

                // Now recalculate totals with the new freight charge
                await this.recalculateTotalsFromUserEdit();
            } else {
                console.log(">>> recalculateFreightChargeFromRate SKIPPED - invalid values (rate or units null/zero)");
            }
        } catch (error) {
            console.error("Error recalculating freight charge from rate:", error);
        }
    }

    /**
     * Recalculates all totals when user manually edits freight charge or related fields.
     * This preserves EDI invoice data on initial load while allowing manual adjustments.
     */
    private async recalculateTotalsFromUserEdit(): Promise<void> {
        if (!this.mainDataSource.isAddingOrUpdating() || this.mainDataSource.activeRow == null) {
            return;
        }
        try {
            // Get current values
            const currentFreightCharge = this.mainDataSource.activeRow?.getCurrency("linehaul_chg");
            const currentOtherCharge = this.mainDataSource.activeRow?.getCurrency("other_charge");
            const currentTaxCharge = this.mainDataSource.activeRow?.getCurrency("excisetax_total");

            if (currentFreightCharge && currentOtherCharge) {
                const freightAmount = currentFreightCharge.amount || 0;
                const otherAmount = currentOtherCharge.amount || 0;
                const taxAmount = currentTaxCharge?.amount || 0;

                // Calculate new total
                const recalculatedTotal = freightAmount + otherAmount;
                const totalWithTax = recalculatedTotal + taxAmount;

                // Update total charges
                const currentTotalCharges = this.mainDataSource.activeRow?.getCurrency("total_charges");
                if (currentTotalCharges) {
                    currentTotalCharges.amount = recalculatedTotal;
                    this.mainDataSource.activeRow?.set("total_charges", currentTotalCharges);
                    this.mainDataSource.activeRow?.set("total_charges_n", recalculatedTotal);
                }

                // Update total with tax if applicable
                if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    this.mainDataSource.activeRow?.set("totalcharge_and_excisetax", totalWithTax);
                }

                // Force display update
                this.mainDataSource.displayDataInBoundComponent(this.textboxTotalCharge);
                if (this.textboxTotalWithTax) {
                    this.mainDataSource.displayDataInBoundComponent(this.textboxTotalWithTax);
                }
            }
        } catch (error) {
            console.error("Error recalculating totals from user edit:", error);
        }
    }
}

